import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
    ShieldCheck, Lock, KeyRound, EyeOff, Bot, Database, Trash2, MapPin, ArrowRight, CircleCheckBig,
} from "lucide-react";

const Section = ({ icon: Icon, title, id, testId, children }) => (
    <section id={id} className="scroll-mt-24 card-soft p-7" data-testid={testId}>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <Icon size={18} />
            </div>
            <h2 className="font-heading font-semibold text-foreground text-xl">{title}</h2>
        </div>
        <div className="mt-4 text-sm text-muted-foreground space-y-3 leading-relaxed">{children}</div>
    </section>
);

const Row = ({ children }) => (
    <div className="inline-flex items-start gap-2 text-sm">
        <CircleCheckBig size={14} className="text-emerald-500 mt-0.5 shrink-0" />
        <span>{children}</span>
    </div>
);

export default function TrustCenter() {
    return (
        <div className="bg-background" data-testid="trust-center-page">
            <Helmet>
                <title>Trust Center · Simonara Talent Solutions</title>
                <meta name="description" content="Plain-English security posture — how Simonara protects candidate and employer data. Encryption, access controls, verification consent, AI data handling, retention, subprocessors, and SOC 2 roadmap." />
                <link rel="canonical" href="/trust-center" />
            </Helmet>

            <section className="pt-16 pb-2">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><ShieldCheck size={11} /> Trust &amp; Security</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">
                        Trust Center
                    </h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        Plain English, no marketing gloss. Everything below is either implemented today or in progress. Version 2026-09-25.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                        <Link
                            to="/privacy"
                            className="btn-outline text-xs px-3 py-1.5"
                            data-testid="trust-link-privacy"
                        >
                            Privacy Policy →
                        </Link>
                        <Link
                            to="/verification-consent-policy"
                            className="btn-outline text-xs px-3 py-1.5"
                            data-testid="trust-link-consent"
                        >
                            Verification &amp; AI Consent →
                        </Link>
                        <Link
                            to="/subprocessors"
                            className="btn-outline text-xs px-3 py-1.5"
                            data-testid="trust-link-subprocessors"
                        >
                            Subprocessors →
                        </Link>
                        <Link
                            to="/dpa"
                            className="btn-outline text-xs px-3 py-1.5"
                            data-testid="trust-link-dpa"
                        >
                            DPA →
                        </Link>
                        <Link
                            to="/data-request"
                            className="btn-outline text-xs px-3 py-1.5"
                            data-testid="trust-link-data-request"
                        >
                            Data Requests →
                        </Link>
                    </div>
                </div>
            </section>

            <section className="pb-20 pt-8">
                <div className="mx-auto max-w-4xl px-6 space-y-4">

                    <Section icon={Lock} id="encryption" title="Encryption" testId="trust-encryption">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Row><strong className="text-foreground">In transit:</strong> TLS 1.2+ on every endpoint. HSTS enabled.</Row>
                            <Row><strong className="text-foreground">At rest:</strong> AES-256 on database volumes and object storage.</Row>
                            <Row><strong className="text-foreground">Sensitive PII fields:</strong> Fernet-encrypted before insert (identity numbers, DOBs).</Row>
                            <Row><strong className="text-foreground">Passwords:</strong> Bcrypt hashed. Plaintext passwords never touch the database or logs.</Row>
                        </div>
                    </Section>

                    <Section icon={EyeOff} id="access" title="Access controls" testId="trust-access">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Row>PII in admin views is <strong className="text-foreground">masked by default</strong> — reveal requires an explicit click.</Row>
                            <Row>Every reveal writes an <strong className="text-foreground">audit log entry</strong> (who · when · which field · which subject).</Row>
                            <Row>Role-based auth (professional · employer · admin · support-admin · platform-admin) — no role can act outside its scope.</Row>
                            <Row>Session tokens rotated on password change or role escalation.</Row>
                            <Row>Production infrastructure access limited to on-call engineers. No standing shell access.</Row>
                        </div>
                    </Section>

                    <Section icon={KeyRound} id="verification-consent" title="Verification consent model" testId="trust-verification">
                        <p>
                            <strong className="text-foreground">Tier 0 contact verification</strong> (US mobile SMS, work-authorization attestation) is part of signup.
                            <strong className="text-foreground"> Tier 1 government-ID verification</strong> happens only on employer request — the candidate completes a secure ID check with our identity provider and explicitly consents; the employer sees only the verified outcome and date, never the ID document.
                            Background checks are available through <strong className="text-foreground">Simonara Recruit</strong>, our done-for-you hiring service — not through the self-serve platform.
                        </p>
                        <p>
                            Read the full{" "}
                            <Link to="/verification-consent-policy" className="underline text-foreground font-medium">Verification &amp; AI Consent Policy</Link>.
                        </p>
                    </Section>

                    <Section icon={Bot} id="ai" title="AI data handling" testId="trust-ai">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Row>Only the fields required for the specific AI feature are sent — <strong className="text-foreground">no bulk resume dumping</strong>.</Row>
                            <Row>OpenAI + Gemini are called in <strong className="text-foreground">zero-retention / no-training</strong> API modes. Your data is not used to train third-party models.</Row>
                            <Row>Generated outputs (hiring briefs, resume drafts, mock-interview scores) are written to your Simonara account only.</Row>
                            <Row>Every AI feature is opt-in and can be turned off per workspace.</Row>
                        </div>
                    </Section>

                    <Section icon={Trash2} id="retention" title="Retention &amp; deletion" testId="trust-retention">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Row>Personal data is <strong className="text-foreground">deleted within 30 days</strong> of account closure — including resumes, chat threads, and passport artifacts.</Row>
                            <Row>Audit logs retained for 12 months for security-incident forensics, then rotated out.</Row>
                            <Row><strong className="text-foreground">ID-verification outcomes</strong> (verified / not verified + date) are kept while the Talent Passport is active and deleted with the account. We never retain ID documents.</Row>
                            <Row>Backups rotate on their normal schedule (max 90 days) after account deletion.</Row>
                        </div>
                    </Section>

                    <Section icon={Database} id="subprocessors" title="Subprocessors" testId="trust-subprocessors-section">
                        <p>Simonara publishes every subprocessor that processes customer data — what they do, what data flows to them, where they operate, and how long they hold it.</p>
                        <p>
                            <Link
                                to="/subprocessors"
                                className="inline-flex items-center gap-1 underline text-foreground font-medium"
                                data-testid="trust-see-subprocessors"
                            >
                                See the full subprocessor list <ArrowRight size={12} />
                            </Link>
                        </p>
                        <p>Customers on a signed <Link to="/dpa" className="underline text-foreground font-medium">DPA</Link> get 30-day notice before any subprocessor change.</p>
                    </Section>

                    <Section icon={MapPin} id="roadmap" title="Roadmap to SOC 2" testId="trust-roadmap">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Row><strong className="text-foreground">Now:</strong> Encryption, access controls, audit logging, DPA + subprocessor register live.</Row>
                            <Row><strong className="text-foreground">In progress:</strong> Formal information-security policy set + vendor risk-review process.</Row>
                            <Row><strong className="text-foreground">Next:</strong> Kick off SOC 2 Type I readiness assessment with a qualified auditor.</Row>
                            <Row><strong className="text-foreground">After Type I:</strong> Begin SOC 2 Type II observation window.</Row>
                        </div>
                        <p className="text-xs italic text-muted-foreground">
                            We won't claim certifications we don't hold and we won't publish target dates we haven't committed to. Progress updates land here as milestones close.
                        </p>
                    </Section>

                    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 inline-flex items-start gap-3">
                        <ShieldCheck size={18} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                        <p className="text-sm text-foreground">
                            Security questions? Email{" "}
                            <a href="mailto:security@simonaratalent.us" className="underline">security@simonaratalent.us</a> — a real human answers within one business day.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
